import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import {
  BadgeButton,
  ServerSidePaginationButtons,
  IconButton,
} from "app/components/atoms/button";
import { FormHeader } from "app/components/atoms/layout";
import { Select, SelectSearch } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import {
  useCollector,
  usePaginatedCollector,
} from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import PDFInvoiceComponent from "app/components/organisms/pdf/pdfInvoice";

const breadcrumbItems = [
  { label: "Sales", href: "#" },
  { label: "Invoice", href: "#" },
];

function getTotal(orders) {
  return orders
    ?.map((order) => {
      return (
        order.OrderItems.map(
          (orderItem) =>
            orderItem.qty * +orderItem.unit_price +
            orderItem.variants
              .map((item) => +item.price)
              .reduce((partialSum, a) => partialSum + a, 0) *
            orderItem.qty
        ).reduce((partialSum, a) => partialSum + a, 0) +
        +order.delivery_fee -
        +order.delivery_fee_discount
      );
    })
    .reduce((partialSum, a) => partialSum + a, 0);
}

function convertPaymentStatus(status) {
  switch (status) {
    case 1:
      return "Unpaid";
    case 2:
      return "Partial";
    case 3:
      return "Lunas";
    case 4:
      return "Cancel";
    default:
      break;
  }
}

function InvoicePage() {
  const navigate = useNavigate();

  const { openModal, setPayloads } = useApplicationModals();
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [params, setParams] = useState({
    search: "",
    status: null,
    customer_id: null,
    page: 1,
    limit: 50,
  });

  const [
    invoices,
    setInvoices,
    invoicesLoader,
    collectInvoices,
    invoicePaginationProps,
  ] = usePaginatedCollector({ modelName: "invoice", params: params });

  useEffect(() => {
    collectInvoices(params);
  }, [params]);

  const [customerParams, setCustomerParams] = useState({
    search: "",
    page: 1,
    limit: Number(process.env.REACT_APP_CUSTOMER_LIMIT),
  });

  const [customers, setMCustomers, customersLoader, collectMCustomers] =
    useCollector({ modelName: "customer", params: customerParams });

  useEffect(() => {
    collectMCustomers(customerParams);
  }, [customerParams]);

  const handleCheckboxClick = (event, item) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedItems([...selectedItems, {
        ...item,
        customer: customers.find((customer) => customer.id === item.customer_id),
      }]);
    } else {
      setSelectedItems(
        selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
      );
    }
  };

  const handleSelectAll = () => {
    setSelectAll(true);
    let selectedInvoices = invoices.map((invoice) => {
      return {
        ...invoice,
        customer: customers.find((customer) => customer.id === invoice.customer_id),
      };
    });
    setSelectedItems(selectedInvoices);
  };

  const handleDeselectAll = () => {
    setSelectAll(false);
    setSelectedItems([]);
  };

  const handleSelectAllCheckboxClick = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      handleSelectAll();
    } else {
      handleDeselectAll();
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="sales"
      activeSidebarChild="invoice"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Daftar Invoice"
      headingButtons={[
        {
          label: "Tambah Invoice",
          type: "routerButton",
          to: "/invoice/create",
        },
        {
          label: "Filter Customer",
          type: "button",
          onClick: () => openModal("customer.choose"),
        },
      ]}
    >
      <AppModals
        items={["invoice.delete", "customer.choose"]}
        onSuccess={{
          "invoice.delete": () => collectInvoices(),
          "customer.choose": (data) => setParams({ ...params, customer_id: data.id }),
        }}
      />
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <FormHeader appearance="none">
              {/* <SearchInput testId="invoice-search-input"
                placeholder="Cari Customer"
                value={params.search}
                onChange={(e) =>
                  setParams({ ...params, search: e.target.value })
                }
              /> */}
              <Select testId="invoice-status-select"
                label="Status"
                placeholder="Status Invoice"
                value={params.status}
                onChange={(e) =>
                  setParams({ ...params, status: e.target.value })
                }
                options={[
                  {
                    value: null,
                    label: "Semua Status",
                  },
                  {
                    value: 1,
                    label: "Unpaid",
                  },
                  {
                    value: 2,
                    label: "Partial",
                  },
                  {
                    value: 3,
                    label: "Lunas",
                  },
                ]}
              />
              <div className="flex items-center">
                <div className="flex-grow w-2/6">
                  <SelectSearch testId="invoice-customer-select"
                    label="Customer"
                    placeholder="Filter Customer"
                    options={customers
                      ?.sort(function (a, b) {
                        if (a.firstName?.toLowerCase() < b.firstName?.toLowerCase())
                          return -1;
                        if (a.firstName?.toLowerCase() > b.firstName?.toLowerCase())
                          return 1;
                        return 0;
                      })
                      .map((customer) => ({
                        value: customer.id,
                        label:
                          (customer.nickName
                            ? " (" + customer.nickName + ") "
                            : "") +
                          customer.firstName +
                          " " +
                          customer.middleName +
                          " " +
                          customer.lastName +
                          (customer.phone ? " - " + customer.phone + "" : ""),
                      }))}
                    value={params.customer_id}
                    onChange={(e) =>
                      setParams({ ...params, customer_id: e.target.value })
                    }
                    onSearch={(text) => {
                      setCustomerParams({ ...customerParams, search: text });
                      if (text == "") {
                        setParams({ ...params, customer_id: null });
                      }
                    }}
                    apiSearch={true}
                    disabled={true}
                  />
                </div>
                <div className="h-full flex items-end">
                  <IconButton testId="invoice-x-mark-button"
                    icon={XMarkIcon}
                    onClick={(e) => {
                      setParams({ ...params, customer_id: null });
                    }}
                  >
                  </IconButton>
                </div>
              </div>
              {/* <Select testId="invoice-customer-select"
                label="Customer"
                placeholder="Customer"
                options={customers
                  ?.sort(function (a, b) {
                    if (
                      a.firstName?.toLowerCase() < b.firstName?.toLowerCase()
                    )
                      return -1;
                    if (
                      a.firstName?.toLowerCase() > b.firstName?.toLowerCase()
                    )
                      return 1;
                    return 0;
                  })
                  ?.map((customer) => ({
                    value: customer.id,
                    label:
                      customer.firstName +
                      " " +
                      customer.middleName +
                      " " +
                      customer.lastName +
                      (customer.nickName
                        ? " (" + customer.nickName + ")"
                        : ""),
                  }))}
                value={params.customer_id}
                onChange={(e) =>
                  setParams({ ...params, customer_id: e.target.value })
                }
              /> */}
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-2 text-gray-700 font-light text-sm">
                  <input data-testid="invoice-chkselectall-input"
                    name="chkSelectAll"
                    id="chkSelectAll"
                    type="checkbox"
                    onChange={handleSelectAllCheckboxClick}
                  />
                  <label htmlFor="chkSelectAll">
                    {selectAll ? "Deselect All" : "Select all"}
                  </label>
                </div>
                <PDFInvoiceComponent invoice={selectedItems} />
              </div>
            </FormHeader>
          </Table.SearchBarWrapper>
          {/* <Table.UtilitiesButtonWrapper>
            <IconButton testId="invoice-printer-button" icon={PrinterIcon} />
          </Table.UtilitiesButtonWrapper> */}
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading></Table.Heading>
              <Table.Heading>No.</Table.Heading>
              <Table.Heading>Customer</Table.Heading>
              <Table.Heading className="hidden lg:table-cell">
                <p className="text-center">Sales</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Total</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Payment</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Kredit</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Kurang / Lebih</p>
              </Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading></Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {invoices?.map((invoice, key) => (
              <Table.BodyRow key={key}>
                <Table.Cell>
                  <input data-testid="invoice-checkbox-input"
                    type="checkbox"
                    checked={selectedItems.some(
                      (selectedInvoice) => selectedInvoice.id === invoice.id
                    )}
                    onChange={(event) =>
                      handleCheckboxClick(event, invoice)
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  <p>{invoice.inv_code}</p>
                  <p className="text-xs font-light text-gray-400">
                    <Typography.Date size="xs" date={invoice.createdAt} />
                  </p>
                </Table.Cell>
                <Table.Cell>
                  <p>
                    {invoice.inv_customer?.firstName}{" "}
                    {invoice.inv_customer?.middleName}{" "}
                    {invoice.inv_customer?.lastName}
                    {invoice.inv_customer?.nickName !== null &&
                      invoice.inv_customer?.nickName !== "" &&
                      " (" + invoice.inv_customer?.nickName + ")"}
                  </p>
                  <p className="text-xs font-light text-gray-400">
                    {invoice.inv_customer?.phone !== null &&
                      invoice.inv_customer?.phone !== "" &&
                      invoice.inv_customer?.phone}
                    {invoice.inv_customer?.deletedAt !== null ? (
                      <span className="text-danger-400 font-light text-xs">
                        {" "}
                        [deleted customer]
                      </span>
                    ) : (
                      ""
                    )}
                  </p>
                </Table.Cell>
                {/* <Table.Cell>
                  <Typography.Date date={invoice.due_date} />
                </Table.Cell> */}
                <Table.Cell className="hidden lg:table-cell">
                  <p className="text-center text-xs text-gray-500 font-light">{invoice?.sales_info?.email}</p>
                  {invoice?.SalesOrders?.map((salesOrder) => (
                    <BadgeButton testId={`invoice-sales-order-update-link-button-${salesOrder.id}`}
                      key={salesOrder.id}
                      href={"/sales_order/update/" + salesOrder.id}
                    >
                      {salesOrder.order_code}
                    </BadgeButton>
                  ))}
                </Table.Cell>
                <Table.Cell>
                  <p className="text-right">
                    <Typography.Currency number={invoice.total_amount} />
                  </p>
                </Table.Cell>
                <Table.Cell>
                  <p className="text-right">
                    <Typography.Currency number={invoice.total_payment} />
                  </p>
                </Table.Cell>
                <Table.Cell>
                  <p className="text-right">
                    <Typography.Currency
                      number={invoice?.credit_amount}
                    ></Typography.Currency>
                  </p>
                </Table.Cell>
                <Table.Cell>
                  <p className="text-right">
                    <Typography.Currency
                      color={
                        Number(invoice.total_amount) -
                          Number(invoice.total_payment) -
                          Number(invoice.credit_amount) ===
                          0
                          ? ""
                          : Number(invoice.total_amount) -
                            Number(invoice.total_payment) -
                            Number(invoice.credit_amount) <
                            0
                            ? "green"
                            : "red"
                      }
                      number={Math.abs(
                        Number(invoice.total_amount) -
                        Number(invoice.total_payment) -
                        Number(invoice.credit_amount)
                      )}
                    />
                  </p>
                </Table.Cell>
                <Table.Cell>
                  {invoice.status === 3 ? (
                    <Badge color="green">
                      {convertPaymentStatus(invoice.status)}
                    </Badge>
                  ) : (
                    <></>
                  )}
                  {invoice.status === 2 ? (
                    <Badge color="yellow">
                      {convertPaymentStatus(invoice.status)}
                    </Badge>
                  ) : (
                    <></>
                  )}
                  {invoice.status === 1 ? (
                    <Badge color="red">
                      {convertPaymentStatus(invoice.status)}
                    </Badge>
                  ) : (
                    <></>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex">
                    <div className="flex space-x-2">
                      <BadgeButton testId="invoice-view-button"
                        onClick={(e) =>
                          navigate("/invoice/detail/" + invoice.id)
                        }
                      >
                        View
                      </BadgeButton>
                    </div>
                    {/* <IconButton testId="invoice-trash-button"
                      icon={TrashIcon}
                      onClick={(e) => {
                        setPayloads("invoice.delete", { id: invoice.id });
                        openModal("invoice.delete");
                      }}
                    /> */}
                  </div>
                </Table.Cell>
              </Table.BodyRow>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={invoicePaginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default InvoicePage;
